import React, { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { API_BASE_URL } from "../services/authService";

type InvitationDetails = {
  invitation?: {
    status?: string;
    appointmentId?: string;
  };
  appointment?: {
    venue?: {
      name?: string;
      address?: string;
      maps_url?: string;
    };
    court?: {
      name?: string;
      full_name?: string;
    };
    service?: {
      name?: string;
    };
    booking?: {
      date?: string;
      time_start?: string;
      time_end?: string;
      time_start_label?: string;
      time_end_label?: string;
    };
    share?: {
      app_url?: string;
    };
  };
  app?: {
    brand?: string | null;
    scheme?: string | null;
    downloadUrl?: string | null;
    schemeUrl?: string | null;
  };
};

const getErrorMessage = (error: any) =>
  error?.response?.data?.message ||
  error?.message ||
  "Something went wrong. Please try again.";

const PointInvitation: React.FC = () => {
  const { token = "" } = useParams();
  const [details, setDetails] = useState<InvitationDetails | null>(null);
  const [loading, setLoading] = useState(true);
  const [declining, setDeclining] = useState(false);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");

  const api = useMemo(
    () =>
      axios.create({
        baseURL: API_BASE_URL,
        timeout: 20000,
      }),
    []
  );

  useEffect(() => {
    let cancelled = false;

    const loadInvitation = async () => {
      setLoading(true);
      setError("");

      try {
        const response = await api.get<InvitationDetails>(
          `/appointments/point-invitations/${encodeURIComponent(token)}`
        );
        if (!cancelled) setDetails(response.data);
      } catch (err: any) {
        if (!cancelled) setError(getErrorMessage(err));
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    if (token) {
      loadInvitation();
    } else {
      setError("Invitation link is invalid.");
      setLoading(false);
    }

    return () => {
      cancelled = true;
    };
  }, [api, token]);

  const handleDecline = async () => {
    setDeclining(true);
    setError("");
    setMessage("");

    try {
      await api.post(
        `/appointments/point-invitations/${encodeURIComponent(token)}/decline`
      );
      setMessage("Invitation declined successfully.");
      setDetails((current) =>
        current
          ? {
              ...current,
              invitation: {
                ...current.invitation,
                status: "DECLINED",
              },
            }
          : current
      );
    } catch (err: any) {
      setError(getErrorMessage(err));
    } finally {
      setDeclining(false);
    }
  };

  const invitation = details?.invitation || {};
  const appointment = details?.appointment || {};
  const venue = appointment.venue || {};
  const court = appointment.court || {};
  const service = appointment.service || {};
  const booking = appointment.booking || {};
  const status = String(invitation.status || "UNKNOWN").toUpperCase();
  // Tenant-resolved by the API from app_versions. Never hardcode a scheme here:
  // each white-label app registers its own ("padel365", "allplay", ...) and the
  // wrong one silently opens whichever other app claims it.
  const appUrl = details?.app?.downloadUrl || appointment.share?.app_url || "";
  const appSchemeUrl = details?.app?.schemeUrl || "";
  const brand = details?.app?.brand || "";
  const isPending = status === "PENDING";

  return (
    <main style={styles.page}>
      <section style={styles.card}>
        <div style={styles.brand}>{brand || "Booking"}</div>
        {loading ? (
          <p style={styles.muted}>Loading invitation...</p>
        ) : error && !details ? (
          <>
            <h1 style={styles.title}>Invitation unavailable</h1>
            <p style={styles.muted}>{error}</p>
          </>
        ) : (
          <>
            <span style={styles.status}>{status}</span>
            <h1 style={styles.title}>You're invited to join this booking</h1>
            {message && <p style={styles.success}>{message}</p>}
            {error && <p style={styles.error}>{error}</p>}

            <InfoRow label="Venue" value={venue.name || "-"} />
            <InfoRow label="Court" value={court.name || court.full_name || "-"} />
            <InfoRow label="Service" value={service.name || "-"} />
            <InfoRow label="Date" value={booking.date || "-"} />
            <InfoRow
              label="Time"
              value={`${booking.time_start_label || booking.time_start || "-"} - ${
                booking.time_end_label || booking.time_end || "-"
              }`}
            />

            <div style={styles.actions}>
              {appSchemeUrl && (
                <a href={appSchemeUrl} style={styles.primary}>
                  Open in app
                </a>
              )}
              {appUrl && (
                <a href={appUrl} style={styles.secondary}>
                  Download app
                </a>
              )}
              {venue.maps_url && (
                <a href={venue.maps_url} style={styles.secondary}>
                  View location
                </a>
              )}
              {isPending && (
                <button
                  type="button"
                  onClick={handleDecline}
                  disabled={declining}
                  style={styles.danger}
                >
                  {declining ? "Declining..." : "Decline invitation"}
                </button>
              )}
            </div>
          </>
        )}
      </section>
    </main>
  );
};

const InfoRow = ({ label, value }: { label: string; value: string }) => (
  <div style={styles.row}>
    <div style={styles.label}>{label}</div>
    <div style={styles.value}>{value}</div>
  </div>
);

const styles: Record<string, React.CSSProperties> = {
  page: {
    minHeight: "100vh",
    background: "#f6f6f6",
    color: "#212121",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: 16,
    boxSizing: "border-box",
  },
  card: {
    width: "100%",
    maxWidth: 460,
    background: "#fff",
    border: "1px solid #e8e8e8",
    borderRadius: 8,
    padding: 20,
    boxShadow: "0 6px 18px rgba(0,0,0,0.08)",
    boxSizing: "border-box",
  },
  brand: {
    background: "#212121",
    color: "#fff",
    borderRadius: 8,
    padding: 16,
    fontSize: 22,
    fontWeight: 700,
    marginBottom: 18,
  },
  status: {
    display: "inline-block",
    padding: "6px 10px",
    borderRadius: 999,
    background: "#f0f0f0",
    fontSize: 12,
    fontWeight: 700,
    marginBottom: 14,
  },
  title: {
    fontSize: 22,
    margin: "0 0 14px",
  },
  row: {
    marginTop: 12,
  },
  label: {
    color: "#666",
    fontSize: 12,
    fontWeight: 700,
    textTransform: "uppercase",
  },
  value: {
    marginTop: 4,
    fontSize: 16,
    fontWeight: 600,
  },
  muted: {
    color: "#666",
    lineHeight: 1.45,
  },
  success: {
    color: "#1b8f5a",
    fontWeight: 700,
  },
  error: {
    color: "#d32f2f",
    fontWeight: 700,
  },
  actions: {
    marginTop: 22,
    display: "grid",
    gap: 10,
  },
  primary: {
    minHeight: 48,
    borderRadius: 8,
    background: "#212121",
    color: "#fff",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    textDecoration: "none",
    fontWeight: 700,
  },
  secondary: {
    minHeight: 48,
    borderRadius: 8,
    background: "#fff",
    color: "#212121",
    border: "1px solid #212121",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    textDecoration: "none",
    fontWeight: 700,
  },
  danger: {
    minHeight: 48,
    borderRadius: 8,
    background: "#fff",
    color: "#d32f2f",
    border: "1px solid #d32f2f",
    fontWeight: 700,
    cursor: "pointer",
  },
};

export default PointInvitation;
